import { useEffect, useState } from "react";
import {
  Activity,
  Bell,
  ChevronRight,
  HardDrive,
  LayoutDashboard,
  LogOut,
  Menu,
  Server as ServerIcon,
  ShieldCheck,
  Settings,
  X,
} from "lucide-react";
import { api, patch, post, setCSRF } from "../api";
import { PageHead } from "../components/common";
import { Avatar, Brand, PageSkeleton, ProgressBar, useActionDialog } from "../components/ui";
import { AuditView, JobsView } from "../features/activity/ActivityViews";
import { Auth, Splash } from "../features/auth/Auth";
import { TwoFactorSettings } from "../features/auth/TwoFactorSettings";
import { Dashboard } from "../features/dashboard/Dashboard";
import { NotificationCenter } from "../features/notifications/NotificationCenter";
import { DiscordSettings } from "../features/system/DiscordSettings";
import { UpdateCenter } from "../features/system/UpdateCenter";
import { CreateServerWizard } from "../features/servers/CreateServerWizard";
import { ServerDetail, ServersView } from "../features/servers/ServerViews";
import type { Server, User } from "../types";
import type { SystemHealth } from "../types";

type View = "dashboard" | "servers" | "jobs" | "audit" | "system" | "settings";
const nav = [
  { id: "dashboard" as View, label: "Visão geral", icon: LayoutDashboard, group: "GERAL" },
  { id: "servers" as View, label: "Servidores", icon: ServerIcon, group: "GERAL" },
  { id: "jobs" as View, label: "Atividades", icon: Activity, group: "OPERAÇÕES" },
  { id: "audit" as View, label: "Auditoria", icon: ShieldCheck, group: "OPERAÇÕES" },
  { id: "system" as View, label: "Sistema", icon: HardDrive, group: "HOST" },
  { id: "settings" as View, label: "Configurações", icon: Settings, group: "CONTA" },
];

export function App() {
  const [boot, setBoot] = useState<{
    setupRequired: boolean;
    agentReady: boolean;
    version: string;
    commit: string;
    buildDate: string;
  } | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    void (async () => {
      try {
        const b = await api<typeof boot>("/system/bootstrap");
        setBoot(b);
        if (!b?.setupRequired) {
          try {
            const me = await api<{ user: User; csrfToken: string }>("/auth/me");
            setCSRF(me.csrfToken);
            setUser(me.user);
          } catch {
            setUser(null);
          }
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  if (loading) return <Splash />;
  if (!user)
    return (
      <Auth
        setup={Boolean(boot?.setupRequired)}
        agentReady={Boolean(boot?.agentReady)}
        onAuth={(u, c) => {
          setCSRF(c);
          setUser(u);
          setBoot((b) => (b ? { ...b, setupRequired: false } : b));
        }}
      />
    );
  return (
    <Shell
      user={user}
      agentReady={Boolean(boot?.agentReady)}
      build={{version:boot?.version??"dev",commit:boot?.commit??"",buildDate:boot?.buildDate??""}}
      onLogout={async () => {
        await post("/auth/logout", {});
        setUser(null);
      }}
    />
  );
}

function Shell({
  user,
  agentReady,
  onLogout,
  build,
}: {
  user: User;
  agentReady: boolean;
  onLogout: () => void;
  build: {version:string;commit:string;buildDate:string};
}) {
  const [view, setView] = useState<View>("dashboard");
  const [mobile, setMobile] = useState(false);
  const [selected, setSelected] = useState<Server | null>(null);
  const [create, setCreate] = useState(false);
  const insecurePublic =
    user.role === "owner" &&
    window.location.protocol === "http:" &&
    !["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
  const navigate = (v: View) => {
    setView(v);
    setSelected(null);
    setMobile(false);
  };
  return (
    <div className="app-shell">
      <aside className={mobile ? "sidebar open" : "sidebar"}>
        <div className="side-head">
          <Brand />
          <button
            className="icon-btn mobile-only"
            onClick={() => setMobile(false)}
            aria-label="Fechar menu"
          >
            <X size={20} />
          </button>
        </div>
        <nav aria-label="Navegação principal">
          {["GERAL", "OPERAÇÕES", "HOST", "CONTA"].map((group) => {
            const items = nav.filter((n) => n.group === group && (n.id !== "system" || user.role === "owner"));
            return items.length ? <div className="nav-group" key={group}><span className="nav-label">{group}</span>{items.map((n) => (
              <button key={n.id} className={view === n.id && !selected ? "nav-item active" : "nav-item"} onClick={() => navigate(n.id)} aria-current={view === n.id && !selected ? "page" : undefined}>
                <n.icon size={18} /><span>{n.label}</span>
              </button>
            ))}</div> : null;
          })}
        </nav>
        <div className="side-foot">
          <div className="host-state">
            <i
              className={agentReady ? "status-dot online" : "status-dot error"}
            />
            <span>
              <b>Host principal</b>
              <small>
                {agentReady ? "Agente conectado" : "Agente offline"}
              </small>
            </span>
          </div>
          <button className="profile" onClick={onLogout}>
            <Avatar name={user.username} size="sm" />
            <span>
              <b>{user.username}</b>
              <small>{roleName(user.role)}</small>
            </span>
            <LogOut size={16} />
          </button>
        </div>
      </aside>
      <main className="workspace">
        <header className="topbar">
          <button
            className="icon-btn mobile-only"
            onClick={() => setMobile(true)}
            aria-label="Abrir menu"
          >
            <Menu size={20} />
          </button>
          <div className="breadcrumbs">
            <span>Reborn</span>
            <ChevronRight size={14} />
            <b>
              {selected
                ? selected.displayName
                : nav.find((n) => n.id === view)?.label}
            </b>
          </div>
          <div className="top-actions">
            {!agentReady && (
              <span className="agent-warning">
                <Bell size={15} />
                Host indisponível
              </span>
            )}
            <NotificationCenter onOpenServers={() => navigate("servers")} />
            <span className="topbar-context">Painel independente para servidores Valheim</span>
          </div>
        </header>
        <div className="page">
          {insecurePublic && (
            <div className="security-banner">
              <ShieldCheck size={16} />
              <span>
                Este painel está usando HTTP. Configure HTTPS antes de
                administrá-lo pela internet.
              </span>
            </div>
          )}
          {selected ? (
            <ServerDetail initial={selected} canManage={user.role === "owner" || user.role === "administrator"} fileRole={user.role} onBack={() => setSelected(null)} />
          ) : view === "dashboard" ? (
            <Dashboard
              onCreate={() => setCreate(true)}
              onSelect={setSelected}
              onShowServers={() => navigate("servers")}
            />
          ) : view === "servers" ? (
            <ServersView
              onCreate={() => setCreate(true)}
              onSelect={setSelected}
            />
          ) : view === "jobs" ? (
            <JobsView />
          ) : view === "settings" ? (
            <SettingsView user={user} build={build} />
          ) : view === "system" ? (
            <SystemView />
          ) : (
            <AuditView />
          )}
        </div>
      </main>
      {mobile && <div className="backdrop" onClick={() => setMobile(false)} />}{" "}
      {create && (
        <CreateServerWizard
          onClose={() => setCreate(false)}
          onCreated={(s) => {
            setCreate(false);
            setSelected(s);
            setView("servers");
          }}
        />
      )}
    </div>
  );
}

function SystemView() {
  const [health, setHealth] = useState<SystemHealth | null>(null);
  useEffect(() => {
    const load = () => void api<SystemHealth>("/system/health").then(setHealth);
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, []);
  if (!health) return <PageSkeleton />;
  const usedMemory = Math.max(0, health.memoryTotalBytes - health.memoryAvailableBytes);
  const uptimeDays = Math.floor(health.uptimeSeconds / 86400);
  return (
    <>
      <PageHead
        eyebrow="SISTEMA"
        title="Saúde do host"
        description="O essencial para saber se seus servidores têm recursos e serviços disponíveis."
      />
      {health.alerts.map((alert) => (
        <div className="notice error" key={alert.kind}>
          <b>{alert.title}</b> {alert.message}
        </div>
      ))}
      <div className="metric-grid">
        <SystemMetric label="Host" value={health.agentConnected ? "Online" : "Indisponível"} />
        <SystemMetric label="Agente" value={health.agentConnected ? "Conectado" : "Indisponível"} />
        <SystemMetric label="Instalação de jogos" value={health.steamcmdReady ? "Pronta" : "Requer atenção"} />
        <SystemMetric label="Servidores" value={`${health.online} online / ${health.servers} total`} />
        <SystemMetric label="CPU" value={`${health.cpuCores || "—"} cores`} />
        <SystemMetric label="Memória em uso" value={`${formatSystemBytes(usedMemory)} / ${formatSystemBytes(health.memoryTotalBytes)}`} progress={health.memoryTotalBytes ? usedMemory / health.memoryTotalBytes * 100 : 0} />
        <SystemMetric label="Disco utilizado" value={`${formatSystemBytes(health.storage.totalBytes-health.storage.freeBytes)} / ${formatSystemBytes(health.storage.totalBytes)}`} progress={health.storage.totalBytes ? (health.storage.totalBytes-health.storage.freeBytes) / health.storage.totalBytes * 100 : 0} />
        <SystemMetric label="Tempo ligado" value={health.uptimeSeconds ? `${uptimeDays} dias` : "Indisponível"} />
        <SystemMetric label="Versão do painel" value={health.panelVersion || "development"} />
      </div>
      <section className="panel system-storage">
        <div className="panel-head">
          <div><span className="eyebrow">ARMAZENAMENTO</span><h2>Uso do disco</h2></div>
          <HardDrive size={20} />
        </div>
        <div className="storage-grid">
          <SystemMetric label="Backups" value={formatSystemBytes(health.storage.backupsBytes)} />
          <SystemMetric label="Mundos" value={formatSystemBytes(health.storage.worldsBytes)} />
          <SystemMetric label="Mods" value={formatSystemBytes(health.storage.modsBytes)} />
          <SystemMetric label="Valheim" value={formatSystemBytes(health.storage.valheimBytes)} />
          <SystemMetric label="Livre" value={formatSystemBytes(health.storage.freeBytes)} />
        </div>
      </section>
    </>
  );
}

function SystemMetric({ label, value, progress }: { label: string; value: string; progress?: number }) {
  return <div className="system-metric"><small>{label}</small><b>{value}</b>{progress != null && <ProgressBar value={progress} label={`${label}: ${Math.round(progress)}%`}/>}</div>;
}

function formatSystemBytes(value: number) {
  if (!value) return "0 GB";
  return `${(value / 1024 ** 3).toFixed(value >= 10 * 1024 ** 3 ? 0 : 1)} GB`;
}

function SettingsView({ user, build }: { user: User; build: { version: string; commit: string; buildDate: string } }) {
  const [host, setHost] = useState("");
  const [saved, setSaved] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    void api<{ publicGameHost: string }>("/system/settings").then((value) => setHost(value.publicGameHost));
  }, [user.role]);
  const save = async () => {
    setError("");
    try {
      const result = await patch<{ publicGameHost: string }>("/system/settings", { publicGameHost: host });
      setHost(result.publicGameHost);
      setSaved("Salvo");
      setTimeout(() => setSaved(""), 1500);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível salvar.");
    }
  };
  const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  return <>
    <PageHead eyebrow="SISTEMA" title="Configurações" description="Preferências globais deste painel."/>
    <div className="settings-layout">
      <nav className="settings-nav" aria-label="Seções de configurações">
        <button onClick={()=>jump("settings-security")}>Segurança</button>
        <button onClick={()=>jump("settings-network")}>Rede</button>
        {user.role === "owner" && <button onClick={()=>jump("settings-discord")}>Discord</button>}
        {user.role === "owner" && <button onClick={()=>jump("settings-users")}>Usuários</button>}
        <button onClick={()=>jump("settings-about")}>Atualizações e sobre</button>
      </nav>
      <div className="settings-content">
      <div id="settings-security" className="section-anchor"><TwoFactorSettings user={user}/></div>
      <section className="panel section-anchor" id="settings-network">
        <div className="panel-head"><div><span className="eyebrow">REDE DOS JOGOS</span><h2>Endereço público</h2></div></div>
        <p>Use o domínio ou IP pelo qual os jogadores alcançam este host. Ele pode ser diferente do domínio do painel.</p>
        <label className="field"><span>Endereço público dos jogos</span><input value={host} disabled={user.role !== "owner"} onChange={(event) => setHost(event.target.value)} placeholder="game.exemplo.com"/></label>
        {user.role === "owner" && <button className="primary small" onClick={() => void save()}>{saved || "Salvar endereço"}</button>}
      </section>
      {user.role === "owner" && <div id="settings-discord" className="section-anchor"><DiscordSettings/></div>}
      {user.role === "owner" && <div id="settings-users" className="section-anchor"><UsersSettings/></div>}
      <div id="settings-about" className="section-anchor">{user.role === "owner" ? <UpdateCenter build={build}/> : <section className="panel"><div className="panel-head"><div><span className="eyebrow">SOBRE</span><h2>Reborn Valheim Control</h2></div></div><dl className="config-list"><div><dt>Versão</dt><dd>{build.version}</dd></div><div><dt>Commit</dt><dd>{build.commit ? build.commit.slice(0, 7) : "development"}</dd></div><div><dt>Build</dt><dd>{build.buildDate || "local"}</dd></div></dl></section>}</div>
      {error && <div className="notice error">{error}</div>}
      </div>
    </div>
  </>;
}

interface ManagedUser { id: string; username: string; role: User["role"]; disabled: boolean; serverIds: string[]; sessions: number; createdAt: number }

function UsersSettings() {
  const { prompt: promptAction, host: dialogHost } = useActionDialog();
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [servers, setServers] = useState<Server[]>([]);
  const [draft, setDraft] = useState({ username: "", password: "", role: "operator" as User["role"], serverIds: [] as string[] });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const load = async () => {
    const [nextUsers, nextServers] = await Promise.all([api<ManagedUser[]>("/users"), api<Server[]>("/servers")]);
    setUsers(nextUsers); setServers(nextServers);
  };
  useEffect(() => { void load(); }, []);
  const create = async () => {
    setError("");
    try { await post("/users", draft); setDraft({ username: "", password: "", role: "operator", serverIds: [] }); setMessage("Usuário criado"); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível criar o usuário."); }
  };
  const update = async (id: string, changes: Partial<Pick<ManagedUser, "role" | "disabled" | "serverIds">>) => {
    setError("");
    try { await patch(`/users/${id}`, changes); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível atualizar o acesso."); }
  };
  const reset = async (id: string) => {
    const password = await promptAction({ title: "Redefinir senha", description: "A senha será alterada e todas as sessões deste usuário serão revogadas.", confirmLabel: "Redefinir senha", input: { label: "Nova senha forte", type: "password", placeholder: "12+ caracteres" } });
    if (!password) return;
    try { await post(`/users/${id}/reset-password`, { newPassword: password }); setMessage("Senha alterada e sessões revogadas"); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível redefinir a senha."); }
  };
  const revoke = async (id: string) => {
    try { await post(`/users/${id}/revoke-sessions`, {}); setMessage("Sessões revogadas"); await load(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Não foi possível revogar as sessões."); }
  };
  const toggleDraft = (id: string) => setDraft({ ...draft, serverIds: draft.serverIds.includes(id) ? draft.serverIds.filter(value => value !== id) : [...draft.serverIds, id] });
  return <>
    <section className="panel users-settings">
      <div className="panel-head"><div><span className="eyebrow">ACESSO</span><h2>Usuários</h2></div><ShieldCheck size={20}/></div>
      <p>Escolha o que cada pessoa pode fazer e quais servidores ela pode ver.</p>
      <div className="user-create">
        <label className="field"><span>Nome</span><input value={draft.username} onChange={(event) => setDraft({ ...draft, username: event.target.value })}/></label>
        <label className="field"><span>Senha inicial</span><input type="password" value={draft.password} onChange={(event) => setDraft({ ...draft, password: event.target.value })}/></label>
        <label className="field"><span>Função</span><select value={draft.role} onChange={(event) => setDraft({ ...draft, role: event.target.value as User["role"] })}><option value="administrator">Administrador</option><option value="operator">Operador</option><option value="viewer">Visualização</option></select></label>
        <div className="server-grants">{servers.map((server) => <label key={server.id}><input type="checkbox" checked={draft.serverIds.includes(server.id)} onChange={() => toggleDraft(server.id)}/>{server.displayName}</label>)}</div>
        <button className="primary small" disabled={!draft.username || !draft.password} onClick={() => void create()}>Criar usuário</button>
      </div>
      <div className="managed-users">{users.map((item) => <div className="user-card" key={item.id}>
        <div><b>{item.username}</b><small>{item.disabled ? "Acesso desativado" : `${item.sessions} sessões ativas`}</small></div>
        <select value={item.role} disabled={item.role === "owner"} onChange={(event) => void update(item.id, { role: event.target.value as User["role"] })}><option value="owner">Proprietário</option><option value="administrator">Administrador</option><option value="operator">Operador</option><option value="viewer">Visualização</option></select>
        {item.role !== "owner" && <div className="server-grants">{servers.map((server) => <label key={server.id}><input type="checkbox" checked={item.serverIds.includes(server.id)} onChange={() => void update(item.id, { serverIds: item.serverIds.includes(server.id) ? item.serverIds.filter(value => value !== server.id) : [...item.serverIds, server.id] })}/>{server.displayName}</label>)}</div>}
        <div className="inline-actions"><button className="secondary small" onClick={() => void reset(item.id)}>Redefinir senha</button><button className="secondary small" onClick={() => void revoke(item.id)}>Revogar sessões</button>{item.role !== "owner" && <button className="text-button" onClick={() => void update(item.id, { disabled: !item.disabled })}>{item.disabled ? "Reativar" : "Desativar"}</button>}</div>
      </div>)}</div>
      {message && <div className="notice success">{message}</div>}{error && <div className="notice error">{error}</div>}
    </section>
    {dialogHost}
  </>;
}
const roleName = (r: string) =>
  ({
    owner: "Owner",
    administrator: "Administrador",
    operator: "Operador",
    viewer: "Visualizador",
  })[r] ?? r;
